import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import App from './App.tsx';
import { UserProvider } from './context/user.context.tsx';
import './index.css';
import { store } from './store/store.ts';
import StyledToaster from './StyledToaster.tsx';
import './utils/i18n.ts';
import { initAuth0Client, setAuthServiceOrigin, setHubApiUrl, setRealmApiId } from '@final-commerce/jwt-storage-lib';
import Gleap from 'gleap';
import React from 'react';
import { redactUrl } from './utils/sentry';
import { ThemeProvider } from './components/ThemeProvider/ThemeProvider.tsx';

setHubApiUrl(import.meta.env.VITE_URL_HUB_API);
setRealmApiId(import.meta.env.VITE_MONGO_API_ID);
// Second identity source (jwt-storage-lib 2.3.0): a session created on the
// app.finalpos.com shell (Better Auth) is refreshed from the auth-service cookie
// and ended on logout. Unset in an environment = Auth0 only, as before.
setAuthServiceOrigin(import.meta.env.VITE_AUTH_SERVICE_ORIGIN);
await initAuth0Client({
  domain: import.meta.env.VITE_AUTH0_DOMAIN,
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
  redirectUri: window.location.origin,
  audience: import.meta.env.VITE_AUTH0_AUDIENCE,
  scope: 'openid profile email offline_access',
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: import.meta.env.VITE_SENTRY_SEND_DEFAULT_PII === 'true',
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
  release: import.meta.env.VITE_SENTRY_RELEASE || 'builder-hub@dev',
  tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE
    ? parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE)
    : 0.0,
  profilesSampleRate: import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE
    ? parseFloat(import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE)
    : 0.0,
  debug: import.meta.env.VITE_SENTRY_DEBUG === 'true',
  integrations: [
    Sentry.browserProfilingIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
      enableReportPageLoaded: true,
      enableInp: true,
      // Ensure proper transaction grouping
      instrumentPageLoad: true,
      instrumentNavigation: true,
    }),
  ],
  // Enable distributed tracing for API requests but exclude auth
  tracePropagationTargets: [
    'localhost',
    '127.0.0.1:8080',
    /^https?:\/\/(?!auth0?\.)[^.]*\.finalpos\.com/,
    /^https:\/\/.*\.finalcommerce\.com/,
    /^https:\/\/.*\.us-central1\.run\.app$/,
  ],
  beforeSend(event) {
    // Add enhanced context to all automatically captured errors
    if (event.exception || event.message) {
      event.tags = {
        ...event.tags,
        component: event.tags?.component || 'AutoCaptured',
      };
      event.extra = {
        ...event.extra,
        url: redactUrl(window.location.href),
      };
    }
    // Redact URLs in request context
    if (event.contexts?.http?.url) {
      event.contexts.http.url = redactUrl(event.contexts.http.url as string);
    }
    return event;
  },
});

Gleap.initialize(import.meta.env.VITE_GLEAP_API_KEY);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <ThemeProvider defaultTheme='system' storageKey='cdk-ui-theme'>
    <UserProvider>
      <Provider store={store}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
        <StyledToaster />
      </Provider>
    </UserProvider>
  </ThemeProvider>,
);
